import { useCallback } from 'react';
import { useParams } from 'react-router';
import { trackEvent } from '~/api/analytics';
import {
  Box,
  Button,
  CardSpinner,
  Center,
  Column,
  ConfirmPopup,
  Row,
  Text,
} from '~/commonComponents';
import browserHistory from '~/history';
import AlertIcon from '~/images/alert-error.svg';
import ConfirmCircle from '~/images/confirm-circle.svg';
import { CircularLoading } from '~/images/lottie';
import { Colors } from '~/themes/colors';
import { CandidateProfile } from '../candidates';
import { ShiftList } from './components';
import { useBulkConfirmation } from './hooks/useBulkConfirmation';
import { useCandidateJobs } from './hooks/useCandidateJobs';
import { useJobSelection } from './hooks/useJobSelection';

export const BulkConfirmCandidateJobs = () => {
  const params = useParams<{ candidateId: string; officeId: string }>();

  const { candidate, jobs, isContractor, isEmployee, isFirstLoading } = useCandidateJobs(
    params.candidateId || '',
    params.officeId || '',
  );

  const {
    checkedJobs,
    clearCheckedJobs,
    hasSelection,
    totalCheckedJobs,
    handleToggleOption,
    isMultipleSelection,
    selectedJobIds,
  } = useJobSelection(jobs);

  const {
    confirmJobs,
    isLoadingJobsConfirmation,
    isJobsConfirmationSuccess,
    bulkConfirmationError,
    handleCloseErrorPopup,
    handleCloseSuccessPopup,
    confirmedJobIds,
  } = useBulkConfirmation({
    candidateId: params.candidateId || '',
  });

  const handleConfirmJobs = useCallback(() => {
    confirmJobs(jobs, selectedJobIds);
    clearCheckedJobs();
  }, [confirmJobs, jobs, selectedJobIds, clearCheckedJobs]);

  if (isFirstLoading || !candidate || !jobs.length) {
    return <CardSpinner />;
  }

  return (
    <>
      <Box className="component_container" justifyContent="flex-start">
        <Column className="floating_container" paddingVertical={36} width="auto">
          <Text bold textAlign="center" fontSize={32} color="neutral_600" marginBottom={24}>
            Confirm Shifts
          </Text>
          <Row paddingHorizontal={40} alignItems="center" justifyContent="space-between">
            <CandidateProfile
              candidate={candidate.candidate}
              jobId={jobs[0].id}
              job={jobs[0].jobApi}
              hasWorkedWithDSO={candidate.hasWorkedWithDSO}
              hasWorkedWithOffice={candidate.hasWorkedWithOffice}
              backgroundCheckStatus={candidate?.backgroundCheckStatus}
            />
            <Row gap={12}>
              <Button
                text="Not Now"
                disabled={isLoadingJobsConfirmation}
                style={{
                  width: 152,
                  height: 48,
                  backgroundColor: Colors.secondary_500,
                  marginTop: 0,
                }}
                onClick={() => {
                  trackEvent('Confirm Shifts - Not now Clicked');

                  browserHistory.push('/');
                }}
              />
              <Button
                disabled={!hasSelection || isLoadingJobsConfirmation || jobs.length === 0}
                text={
                  totalCheckedJobs > 0 && isContractor ? `Confirm (${totalCheckedJobs})` : 'Confirm'
                }
                style={{
                  width: 152,
                  height: 48,
                  marginTop: 0,
                }}
                onClick={handleConfirmJobs}
              />
            </Row>
          </Row>
          <Center paddingHorizontal={114} marginVertical={32}>
            <Text color="neutral_500" fontSize={16} fontStyle="italic" textAlign="center">
              The list below displays additional offers that {candidate.candidate.first_name}{' '}
              {candidate.candidate.last_name} has submitted for other shifts at your office. Review
              the candidate&apos;s application details, including any counters they submitted for
              the hourly rate and/or shift hours.
            </Text>
          </Center>
          <ShiftList
            candidate={candidate.candidate}
            checkedJobs={checkedJobs}
            isMultipleSelection={isMultipleSelection}
            jobs={jobs}
            handleToggleOption={handleToggleOption}
          />
        </Column>
      </Box>
      {isLoadingJobsConfirmation && (
        // @ts-expect-error -- ConfirmPopup still written in Javascript
        <ConfirmPopup
          Icon={() => (
            <Center>
              <CircularLoading />
            </Center>
          )}
          title="Processing..."
          titleStyle={{ marginTop: 36, marginBottom: 8 }}
          description="Please, wait while we confirm your selected shifts. Thank you for your patience."
          descriptionStyle={{ color: Colors.neutral_500, marginBottom: 0 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
        />
      )}
      {isJobsConfirmationSuccess && (
        // @ts-expect-error -- ConfirmPopup still written in Javascript
        <ConfirmPopup
          Icon={() => (
            <Center>
              <img alt="confirm_circle" src={ConfirmCircle} />
            </Center>
          )}
          title="Good News!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          description={
            isEmployee
              ? 'Your shift has successfully been filled.'
              : // eslint-disable-next-line max-len
                `${candidate.candidate.first_name} ${candidate.candidate.last_name} has been confirmed for ${confirmedJobIds.length} shift(s).`
          }
          descriptionStyle={{ color: Colors.neutral_500, marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Ok"
          rightButtonAction={handleCloseSuccessPopup}
        />
      )}

      {bulkConfirmationError && (
        // @ts-expect-error -- ConfirmPopup still written in Javascript
        <ConfirmPopup
          title="Sorry!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          Icon={() => <img src={AlertIcon} alt="AlertIcon" />}
          description={
            bulkConfirmationError.message ||
            'Unable to confirm any of the selected shifts. Please try again later.'
          }
          descriptionStyle={{ color: Colors.neutral_500, marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Close"
          rightButtonAction={handleCloseErrorPopup}
        />
      )}
    </>
  );
};
